import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multiselect';
import { Filter, X, RotateCcw } from 'lucide-react';

// ─── Filter types ──────────────────────────────────────────────────────────────

export interface PRMultiSelectFilter {
  kind: 'multi-select';
  selected: string[];
}

export interface PRNumericRangeFilter {
  kind: 'numeric-range';
  operator: 'between' | 'gte' | 'lte';
  min?: number;
  max?: number;
}

export interface PRDateRangeFilter {
  kind: 'date-range';
  from?: string; // ISO date string
  to?: string;
}

export type PRFilterValue = PRMultiSelectFilter | PRNumericRangeFilter | PRDateRangeFilter;
export type PRFilters = Record<string, PRFilterValue>;

/** Maximum absolute value for numeric filter inputs (prevents scientific notation overflow) */
const NUMERIC_FILTER_MAX_VALUE = 999_999_999_999;

// ─── Helpers ───────────────────────────────────────────────────────────────────

export function countActivePRFilters(filters: PRFilters): number {
  let count = 0;
  for (const f of Object.values(filters)) {
    if (f.kind === 'multi-select' && f.selected.length > 0) count++;
    if (f.kind === 'numeric-range' && (f.min !== undefined || f.max !== undefined)) count++;
    if (f.kind === 'date-range' && (f.from || f.to)) count++;
  }
  return count;
}

/** Apply all active PR filters to a dataset */
export function applyPRFilters<T extends {
  status: string;
  supplier: string;
  category: string;
  amount: number;
  date: string;
  requestedBy?: string;
  orderNumber?: string;
  poNumber?: string[];
  costCentreCode?: string;
  costCentreName?: string;
  approvalHistory?: { toUser?: string | string[]; byUser?: string; stage?: string; date: string; action: string; status: string }[];
}>(
  items: T[],
  filters: PRFilters,
  resolveUser?: (id: string) => string,
  helpers?: {
    getCurrentApprover?: (item: T) => string;
    getLastApprover?: (item: T) => string;
    getCurrentStage?: (item: T) => string;
    calculateAge?: (item: T) => number | null;
  }
): T[] {
  const activeEntries = Object.entries(filters).filter(([, f]) => {
    if (f.kind === 'multi-select') return f.selected.length > 0;
    if (f.kind === 'numeric-range') return f.min !== undefined || f.max !== undefined;
    if (f.kind === 'date-range') return !!(f.from || f.to);
    return false;
  });
  if (activeEntries.length === 0) return items;

  return items.filter((item) =>
    activeEntries.every(([colId, filter]) => {
      if (filter.kind === 'multi-select') {
        switch (colId) {
          case 'status':
            return filter.selected.includes(item.status);
          case 'supplier': {
            const name = resolveUser ? resolveUser(item.supplier) : item.supplier;
            return filter.selected.includes(item.supplier) || filter.selected.includes(name);
          }
          case 'category':
            return filter.selected.includes(item.category);
          case 'requestedBy': {
            const name = resolveUser ? resolveUser(item.requestedBy ?? '') : (item.requestedBy ?? '');
            return filter.selected.includes(item.requestedBy ?? '') || filter.selected.includes(name);
          }
          case 'costCentre':
            return filter.selected.includes(item.costCentreCode ?? '');
          case 'currentStage': {
            const stage = helpers?.getCurrentStage ? helpers.getCurrentStage(item) : '';
            return filter.selected.includes(stage);
          }
          case 'currentApprover': {
            const approver = helpers?.getCurrentApprover ? helpers.getCurrentApprover(item) : '';
            return filter.selected.includes(approver);
          }
          case 'lastApprover': {
            const last = helpers?.getLastApprover ? helpers.getLastApprover(item) : '';
            return filter.selected.includes(last);
          }
          case 'orderNumber': {
            const orderNums = item.poNumber ?? (item.orderNumber ? [item.orderNumber] : []);
            return orderNums.some((o) => filter.selected.includes(o));
          }

          default:
            return true;
        }
      }
      if (filter.kind === 'numeric-range') {
        let v: number | null = null;
        if (colId === 'amount') v = item.amount;
        else if (colId === 'daysOpen') v = helpers?.calculateAge ? helpers.calculateAge(item) : null;
        if (v === null) return true;
        if (filter.operator === 'between') {
          if (filter.min !== undefined && v < filter.min) return false;
          if (filter.max !== undefined && v > filter.max) return false;
          return true;
        }
        if (filter.operator === 'gte') return filter.min === undefined || v >= filter.min;
        if (filter.operator === 'lte') return filter.max === undefined || v <= filter.max;
        return true;
      }
      if (filter.kind === 'date-range' && colId === 'date') {
        const d = new Date(item.date).getTime();
        if (filter.from && d < new Date(filter.from).getTime()) return false;
        if (filter.to && d > new Date(filter.to + 'T23:59:59').getTime()) return false;
        return true;
      }
      return true;
    })
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

interface PurchaseRequestFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: PRFilters;
  onApply: (filters: PRFilters) => void;
  onClear: () => void;
  /** Available options derived from data */
  availableStatuses: string[];
  availableSuppliers: { value: string; label: string; code?: string; name?: string }[];
  availableCategories: string[];
  availableRequestedBy?: { value: string; label: string }[];
  availableCostCentres?: { value: string; label: string }[];
  availableCurrentStages?: string[];
  availableCurrentApprovers?: string[];
  availableLastApprovers?: string[];
}

export function PurchaseRequestFilterSheet({
  open,
  onOpenChange,
  filters: appliedFilters,
  onApply,
  onClear,
  availableStatuses,
  availableSuppliers,
  availableCategories,
  availableRequestedBy = [],
  availableCostCentres = [],
  availableCurrentStages = [],
  availableCurrentApprovers = [],
  availableLastApprovers = [],
}: PurchaseRequestFilterSheetProps) {
  // Local working copy — only committed on Apply
  const [draft, setDraft] = useState<PRFilters>(appliedFilters);

  // Sync draft when sheet opens
  useEffect(() => {
    if (open) setDraft(appliedFilters);
  }, [open, appliedFilters]);

  // ── helpers ──────────────────────────────────────────────────────────

  const getMultiSelect = (key: string): PRMultiSelectFilter =>
    (draft[key] as PRMultiSelectFilter) ?? { kind: 'multi-select', selected: [] };

  const setMultiSelect = (key: string, selected: string[]) =>
    setDraft((prev) => ({ ...prev, [key]: { kind: 'multi-select', selected } }));

  const getNumericRange = (key: string): PRNumericRangeFilter =>
    (draft[key] as PRNumericRangeFilter) ?? { kind: 'numeric-range', operator: 'between' };

  const getDateRange = (key: string): PRDateRangeFilter =>
    (draft[key] as PRDateRangeFilter) ?? { kind: 'date-range' };

  const setNumericRange = (key: string, field: 'min' | 'max', raw: string) => {
    if (raw === '') {
      setDraft((prev) => {
        const existing = getNumericRange(key);
        return { ...prev, [key]: { ...existing, [field]: undefined } };
      });
      return;
    }
    const parsed = parseFloat(raw);
    if (!isFinite(parsed) || Math.abs(parsed) > NUMERIC_FILTER_MAX_VALUE) return;
    setDraft((prev) => {
      const existing = getNumericRange(key);
      return { ...prev, [key]: { ...existing, [field]: parsed } };
    });
  };

  const setNumericOperator = (key: string, operator: PRNumericRangeFilter['operator']) => {
    setDraft((prev) => {
      const existing = getNumericRange(key);
      return { ...prev, [key]: { ...existing, operator } };
    });
  };

  const setDateField = (key: string, field: 'from' | 'to', value: string) => {
    setDraft((prev) => {
      const existing = getDateRange(key);
      return { ...prev, [key]: { ...existing, [field]: value || undefined } };
    });
  };

  const clearSection = (key: string) => {
    setDraft((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleApply = () => {
    onApply(draft);
    onOpenChange(false);
  };

  const handleClear = () => {
    setDraft({});
    onClear();
    onOpenChange(false);
  };

  const activeCount = countActivePRFilters(draft);


  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[520px] sm:w-[560px] p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <SheetTitle>Advanced Filters</SheetTitle>
              {activeCount > 0 && (
                <Badge variant="default" className="h-5 text-xs px-1.5">
                  {activeCount}
                </Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground"
              onClick={handleClear}
              disabled={activeCount === 0 && countActivePRFilters(appliedFilters) === 0}
            >
              <RotateCcw className="h-3 w-3" />
              Clear all
            </Button>
          </div>
        </SheetHeader>

        {/* Body */}
        <ScrollArea className="flex-1">
          <ScrollAreaViewport>
            <div className="px-6 py-4 space-y-6">

              {/* ── Status ─────────────────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Status</Label>
                  {getMultiSelect('status').selected.length > 0 && (
                    <button
                      type="button"
                      onClick={() => clearSection('status')}
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                    >
                      <X className="h-3 w-3" /> Clear
                    </button>
                  )}
                </div>
                <MultiSelect
                  options={availableStatuses.map((s) => ({ value: s, label: s }))}
                  placeholder="Select statuses..."
                  value={getMultiSelect('status').selected}
                  onValueChange={(selected) =>
                    setDraft((prev) => ({ ...prev, status: { kind: 'multi-select', selected } }))
                  }
                />
              </div>

              <Separator />

              {/* ── Supplier ───────────────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Supplier</Label>
                  {getMultiSelect('supplier').selected.length > 0 && (
                    <button
                      type="button"
                      onClick={() => clearSection('supplier')}
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                    >
                      <X className="h-3 w-3" /> Clear
                    </button>
                  )}
                </div>
                <MultiSelect
                  options={availableSuppliers}
                  placeholder="Select suppliers..."
                  value={getMultiSelect('supplier').selected}
                  onValueChange={(selected) =>
                    setDraft((prev) => ({ ...prev, supplier: { kind: 'multi-select', selected } }))
                  }
                  renderBadgeLabel={(opt) => (opt as { code?: string; name?: string; label: string }).code ?? opt.label}
                  renderOption={(opt) => {
                    const o = opt as { code?: string; name?: string; label: string };
                    return o.code ? (
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium truncate">{o.name}</span>
                        <span className="text-xs text-muted-foreground">{o.code}</span>
                      </div>
                    ) : (
                      <span className="text-sm">{opt.label}</span>
                    );
                  }}
                />
              </div>

              <Separator />

              {/* ── Category (column-bound) ───────────────────────── */}
              {availableCategories.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm font-medium">Category</Label>
                    {getMultiSelect('category').selected.length > 0 && (
                      <button
                        type="button"
                        onClick={() => clearSection('category')}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    )}
                  </div>
                  <MultiSelect
                    options={availableCategories.map((c) => ({ value: c, label: c }))}
                    value={getMultiSelect('category').selected}
                    onValueChange={(vals) => setMultiSelect('category', vals)}
                    placeholder="Select categories..."
                  />
                </div>
              )}

              {availableCategories.length > 0 && <Separator />}

              {/* ── Requested By ──────────────────────────────────── */}
              {availableRequestedBy.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Requested By</Label>
                    {getMultiSelect('requestedBy').selected.length > 0 && (
                      <button
                        type="button"
                        onClick={() => clearSection('requestedBy')}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    )}
                  </div>
                  <MultiSelect
                    options={availableRequestedBy}
                    placeholder="Select employees..."
                    value={getMultiSelect('requestedBy').selected}
                    onValueChange={(vals) => setMultiSelect('requestedBy', vals)}
                  />
                </div>
              )}

              {availableRequestedBy.length > 0 && <Separator />}

              {/* ── Cost Centre ───────────────────────────────────── */}
              {availableCostCentres.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Cost Centre</Label>
                    {getMultiSelect('costCentre').selected.length > 0 && (
                      <button
                        type="button"
                        onClick={() => clearSection('costCentre')}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    )}
                  </div>
                  <MultiSelect
                    options={availableCostCentres}
                    placeholder="Select cost centres..."
                    value={getMultiSelect('costCentre').selected}
                    onValueChange={(vals) => setMultiSelect('costCentre', vals)}
                  />
                </div>
              )}

              {availableCostCentres.length > 0 && <Separator />}

              {/* ── Current Stage ─────────────────────────────────── */}
              {availableCurrentStages.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Current Stage</Label>
                    {getMultiSelect('currentStage').selected.length > 0 && (
                      <button
                        type="button"
                        onClick={() => clearSection('currentStage')}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    )}
                  </div>
                  <MultiSelect
                    options={availableCurrentStages.map((s) => ({ value: s, label: s }))}
                    placeholder="Select stages..."
                    value={getMultiSelect('currentStage').selected}
                    onValueChange={(vals) => setMultiSelect('currentStage', vals)}
                  />
                </div>
              )}

              {availableCurrentStages.length > 0 && <Separator />}

              {/* ── Current Approver ──────────────────────────────── */}
              {availableCurrentApprovers.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Current Approver</Label>
                    {getMultiSelect('currentApprover').selected.length > 0 && (
                      <button
                        type="button"
                        onClick={() => clearSection('currentApprover')}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    )}
                  </div>
                  <MultiSelect
                    options={availableCurrentApprovers.map((a) => ({ value: a, label: a }))}
                    placeholder="Select approvers..."
                    value={getMultiSelect('currentApprover').selected}
                    onValueChange={(vals) => setMultiSelect('currentApprover', vals)}
                  />
                </div>
              )}

              {availableCurrentApprovers.length > 0 && <Separator />}

              {/* ── Last Approver ─────────────────────────────────── */}
              {availableLastApprovers.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium">Last Approver</Label>
                    {getMultiSelect('lastApprover').selected.length > 0 && (
                      <button
                        type="button"
                        onClick={() => clearSection('lastApprover')}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    )}
                  </div>
                  <MultiSelect
                    options={availableLastApprovers.map((a) => ({ value: a, label: a }))}
                    placeholder="Select approvers..."
                    value={getMultiSelect('lastApprover').selected}
                    onValueChange={(vals) => setMultiSelect('lastApprover', vals)}
                  />
                </div>
              )}

              {availableLastApprovers.length > 0 && <Separator />}

              {/* ── Amount ────────────────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-medium">Amount</Label>
                  {(() => {
                    const f = getNumericRange('amount');
                    const active = f.min !== undefined || f.max !== undefined;
                    return active ? (
                      <button
                        type="button"
                        onClick={() => clearSection('amount')}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    ) : null;
                  })()}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Select
                    value={getNumericRange('amount').operator}
                    onValueChange={(v) => setNumericOperator('amount', v as PRNumericRangeFilter['operator'])}
                  >
                    <SelectTrigger className="h-8 text-sm w-36 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="between">Between</SelectItem>
                      <SelectItem value="gte">≥ Greater than</SelectItem>
                      <SelectItem value="lte">≤ Less than</SelectItem>
                    </SelectContent>
                  </Select>
                  {getNumericRange('amount').operator === 'between' ? (
                    <>
                      <Input
                        type="number"
                        placeholder="Min"
                        className="h-8 text-sm"
                        value={getNumericRange('amount').min ?? ''}
                        onChange={(e) => setNumericRange('amount', 'min', e.target.value)}
                      />
                      <span className="text-muted-foreground text-xs shrink-0">–</span>
                      <Input
                        type="number"
                        placeholder="Max"
                        className="h-8 text-sm"
                        value={getNumericRange('amount').max ?? ''}
                        onChange={(e) => setNumericRange('amount', 'max', e.target.value)}
                      />
                    </>
                  ) : (
                    <Input
                      type="number"
                      placeholder="Amount"
                      className="h-8 text-sm"
                      value={
                        getNumericRange('amount').operator === 'gte'
                          ? (getNumericRange('amount').min ?? '')
                          : (getNumericRange('amount').max ?? '')
                      }
                      onChange={(e) =>
                        setNumericRange(
                          'amount',
                          getNumericRange('amount').operator === 'gte' ? 'min' : 'max',
                          e.target.value
                        )
                      }
                    />
                  )}
                </div>
              </div>

              <Separator />

              {/* ── Date ─────────────────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-medium">Date Range</Label>
                  {(getDateRange('date').from || getDateRange('date').to) && (
                    <button
                      type="button"
                      onClick={() => clearSection('date')}
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                    >
                      <X className="h-3 w-3" /> Clear
                    </button>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground">From</Label>
                    <Input
                      type="date"
                      className="h-8 text-sm mt-1"
                      value={getDateRange('date').from ?? ''}
                      onChange={(e) => setDateField('date', 'from', e.target.value)}
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-muted-foreground">To</Label>
                    <Input
                      type="date"
                      className="h-8 text-sm mt-1"
                      value={getDateRange('date').to ?? ''}
                      onChange={(e) => setDateField('date', 'to', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* ── Days Open ─────────────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-medium">Days Open</Label>
                  {(() => {
                    const f = getNumericRange('daysOpen');
                    const active = f.min !== undefined || f.max !== undefined;
                    return active ? (
                      <button
                        type="button"
                        onClick={() => clearSection('daysOpen')}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-0.5"
                      >
                        <X className="h-3 w-3" /> Clear
                      </button>
                    ) : null;
                  })()}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Select
                    value={getNumericRange('daysOpen').operator}
                    onValueChange={(v) => setNumericOperator('daysOpen', v as PRNumericRangeFilter['operator'])}
                  >
                    <SelectTrigger className="h-8 text-sm w-36 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="between">Between</SelectItem>
                      <SelectItem value="gte">≥ Greater than</SelectItem>
                      <SelectItem value="lte">≤ Less than</SelectItem>
                    </SelectContent>
                  </Select>
                  {getNumericRange('daysOpen').operator === 'between' ? (
                    <>
                      <Input
                        type="number"
                        placeholder="Min days"
                        className="h-8 text-sm"
                        value={getNumericRange('daysOpen').min ?? ''}
                        onChange={(e) => setNumericRange('daysOpen', 'min', e.target.value)}
                      />
                      <span className="text-muted-foreground text-xs shrink-0">–</span>
                      <Input
                        type="number"
                        placeholder="Max days"
                        className="h-8 text-sm"
                        value={getNumericRange('daysOpen').max ?? ''}
                        onChange={(e) => setNumericRange('daysOpen', 'max', e.target.value)}
                      />
                    </>
                  ) : (
                    <Input
                      type="number"
                      placeholder="Days"
                      className="h-8 text-sm"
                      value={
                        getNumericRange('daysOpen').operator === 'gte'
                          ? (getNumericRange('daysOpen').min ?? '')
                          : (getNumericRange('daysOpen').max ?? '')
                      }
                      onChange={(e) =>
                        setNumericRange(
                          'daysOpen',
                          getNumericRange('daysOpen').operator === 'gte' ? 'min' : 'max',
                          e.target.value
                        )
                      }
                    />
                  )}
                </div>
              </div>

            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>

        {/* Footer */}
        <SheetFooter className="px-6 py-4 border-t flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={handleApply}>
            Apply Filters
            {activeCount > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 text-xs px-1.5">
                {activeCount}
              </Badge>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
